/**
 * optimistic_activity — predict the activity row's new state for the
 * field the user just edited, render it BEFORE the network confirms,
 * reconcile with the server's authoritative response when it lands.
 *
 * Why: the network round-trip + backend CPM is ~100–500ms. Without
 * optimistic UI, the row appears frozen during that window — clicks
 * feel laggy. With optimistic UI, the row updates on the next
 * animation frame (~16ms) and the server's authoritative value
 * silently overwrites if it differs.
 *
 * Predictions are deliberately NAÏVE:
 *   - Date math is calendar-day, not working-day. The server applies
 *     the activity's calendar on its own and may shift the finish by
 *     a day or two for weekends/holidays. The reconciliation handles
 *     this — the user sees the corrected value when the response lands.
 *   - Cascaded successor changes are NOT predicted. FE doesn't have
 *     the CPM engine. Successors update when the server response
 *     lands (~250ms after the optimistic render). User perceives
 *     "edit was instant; chain updated a quarter-second later" —
 *     which feels live, not slow.
 *
 * For fields where prediction is risky (resources, calendar_id,
 * complex CPM-touched fields), we return the activity unchanged —
 * the user sees the row "save" but no value flicker until the server
 * response. This is acceptable; the win is on the common fields
 * (date / duration / status / progress) which are the editing hot path.
 */
import { parseISO, addDays, format } from 'date-fns';

function safeAddDays(isoOrIsoDateString, daysToAdd) {
  if (!isoOrIsoDateString) return null;
  // Tolerate both 'YYYY-MM-DD' and 'YYYY-MM-DDTHH:mm:ss' shapes.
  const ymd = isoOrIsoDateString.slice(0, 10);
  const parsed = parseISO(ymd);
  return format(addDays(parsed, Math.max(0, daysToAdd)), 'yyyy-MM-dd');
}

/**
 * Predict the activity object the server will return after applying
 * a single-field edit. Returns a new object with the predicted fields
 * merged onto the activity. NEVER mutates the input.
 *
 * Unknown fields fall through to the unchanged activity — the server
 * response will populate them in the reconciliation step.
 */
export function predictOptimisticUpdate(activity, field, value) {
  if (!activity) return activity;
  switch (field) {
    case 'original_duration':
    case 'duration': {
      const newDuration = parseInt(value, 10);
      if (!Number.isFinite(newDuration) || newDuration < 1) return activity;
      const start = activity.actual_start || activity.planned_start || activity.early_start;
      const naiveFinish = safeAddDays(start, Math.max(0, newDuration - 1));
      return {
        ...activity,
        original_duration: newDuration,
        remaining_duration: newDuration,
        planned_finish: naiveFinish || activity.planned_finish,
      };
    }

    case 'planned_start':
    case 'start_date': {
      // value: 'YYYY-MM-DD'
      if (!value) return activity;
      const dur = activity.original_duration || activity.remaining_duration || 1;
      const naiveFinish = safeAddDays(value, Math.max(0, dur - 1));
      return {
        ...activity,
        planned_start: value,
        planned_finish: naiveFinish || activity.planned_finish,
      };
    }

    case 'planned_finish':
    case 'finish_date': {
      if (!value) return activity;
      // Don't predict duration change here — the server's behaviour is
      // (start fixed, duration recomputed) and we don't want to flash a
      // wrong duration. Just update the finish.
      return { ...activity, planned_finish: value };
    }

    case 'actual_start':
      return { ...activity, actual_start: value };

    case 'actual_finish':
      return { ...activity, actual_finish: value };

    case 'status':
      return { ...activity, status: value };

    case 'percent_complete': {
      const n = parseFloat(value);
      if (!Number.isFinite(n)) return activity;
      return { ...activity, percent_complete: Math.max(0, Math.min(100, n)) };
    }

    case 'name':
      return { ...activity, name: value };

    case 'expected_finish':
      return { ...activity, expected_finish: value };

    default:
      // Unknown field — fall through to server response.
      return activity;
  }
}

export default predictOptimisticUpdate;
